// Component imports - Triathlon checklist management system
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Section } from '@/components/Section';
import { SharedSection } from '@/components/SharedSection';
import { SectionSkeleton } from '@/components/SectionSkeleton';
import { CanonicalTag } from '@/components/CanonicalTag';
import { StructuredData } from '@/components/StructuredData';
import { ChecklistModal } from '@/components/ChecklistModal';
import { SampleChecklistModal } from '@/components/SampleChecklistModal';
import { RacePhaseCarousel } from '@/components/RacePhaseCarousel';
import { SeoChecklistContent } from '@/components/SeoChecklistContent';
import { BackgroundImageUploader } from '@/components/BackgroundImageUploader';
import { useBackgroundImage } from '@/hooks/useBackgroundImage';
import { HeroSection } from '@/components/HeroSection';
import { trackEvent } from '@/components/GoogleAnalytics';

// Guest mode sessionStorage helpers
const GUEST_SECTION_KEY = 'guest-checklist-section';
const GUEST_PHASES_KEY = 'guest-checklist-phases';

interface GuestSection {
  id: string;
  name: string;
  targetDate: string | null;
  countdownEnabled: boolean;
}

interface GuestPhase {
  id: string;
  title: string;
}

const saveGuestSection = (section: GuestSection) => {
  sessionStorage.setItem(GUEST_SECTION_KEY, JSON.stringify(section));
};

const loadGuestSection = (): GuestSection | null => {
  const data = sessionStorage.getItem(GUEST_SECTION_KEY);
  return data ? JSON.parse(data) : null;
};

const saveGuestPhases = (phases: GuestPhase[]) => {
  sessionStorage.setItem(GUEST_PHASES_KEY, JSON.stringify(phases));
};

const loadGuestPhases = (): GuestPhase[] => {
  const data = sessionStorage.getItem(GUEST_PHASES_KEY);
  return data ? JSON.parse(data) : [];
};

const clearGuestData = () => {
  // Load phases FIRST while they still exist
  const guestPhases = loadGuestPhases();
  
  // Clear phase items
  guestPhases.forEach(phase => {
    sessionStorage.removeItem(`checklist-items-${phase.id}`);
  });
  
  // Now remove section and phases metadata
  sessionStorage.removeItem(GUEST_SECTION_KEY);
  sessionStorage.removeItem(GUEST_PHASES_KEY);
};


import { SearchModal } from '@/components/SearchModal';
import { OnboardingModal } from '@/components/OnboardingModal';
import { ShareModal } from '@/components/ShareModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Printer, Plus, LogIn, LogOut, Target, Search, HelpCircle, Bike, ChevronDown, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { AdminTriathlonPanel } from '@/components/AdminTriathlonPanel';
import { AdminPanel } from '@/components/AdminPanel';
import { CountdownBanner } from '@/components/CountdownBanner';
import { useAuth } from '@/hooks/useAuth';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useDataPreloader } from '@/hooks/useDataPreloader';
import { escapeHtml, safeDisplayText, safeSaveText, safePrintText, validateFormInput } from '@/lib/utils';
import { secureLog } from '@/lib/security';
import { useSharing } from '@/hooks/useSharing';

interface Phase {
  id: string;
  title: string;
  isPlaceholder?: boolean;
}

interface Section {
  id: string;
  name: string;
  phases: Phase[];
  targetDate?: Date | null;
  countdownEnabled?: boolean;
}


// Function to get the dynamic triathlon race year
const getTriathlonYear = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const september = new Date(currentYear, 8); // September is month 8 (0-indexed)
  const secondSunday = new Date(september);
  
  // Find the second Sunday of September
  const firstDay = new Date(september.getFullYear(), september.getMonth(), 1);
  const firstSunday = new Date(firstDay);
  firstSunday.setDate(firstDay.getDate() + (7 - firstDay.getDay()) % 7);
  secondSunday.setDate(firstSunday.getDate() + 7);
  
  // If current date is after second Sunday of September, use next year
  return now > secondSunday ? currentYear + 1 : currentYear;
};

const DEFAULT_SECTIONS: Section[] = [
  {
    id: 'section-1',
    name: 'My Event',
    phases: [
      { id: 'checklist-1', title: 'Checklist 1' },
      { id: 'placeholder-1', title: 'Add Checklist', isPlaceholder: true }
    ]
  }
];

interface ChecklistItem {
  id: string;
  item: string;
  comment: string;
}

const Checklists = () => {
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
  const [selectedPhaseTitle, setSelectedPhaseTitle] = useState<string>('');
  const [phaseTitles, setPhaseTitles] = useState<Record<string, string>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sections, setSections] = useState<Section[]>([]);
  const [isModalDataLoading, setIsModalDataLoading] = useState(false);
  const [preloadedModalData, setPreloadedModalData] = useState<any[]>([]);
  const [newlyCreatedSectionId, setNewlyCreatedSectionId] = useState<string | null>(null);
  const [showSampleSection, setShowSampleSection] = useState(true);
  const [sampleSectionCollapsed, setSampleSectionCollapsed] = useState(false);
  const [combinedSectionCollapsed, setCombinedSectionCollapsed] = useState(true);
  const [showSamplesAndTemplates, setShowSamplesAndTemplates] = useState(() => {
    // Check localStorage for explicit user preference
    const savedPreference = localStorage.getItem('samples-templates-hidden');
    if (savedPreference === 'true') {
      return false; // User explicitly hid it before
    }
    return true; // Show by default for new visitors
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [sectionCountdowns, setSectionCountdowns] = useState<Record<string, { targetDate: Date | null; enabled: boolean }>>({});
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [userSectionCollapsed, setUserSectionCollapsed] = useState<Record<string, boolean>>({});
  // Dynamic phases loaded from admin data
  const [sampleTriathlonPhases, setSampleTriathlonPhases] = useState<Phase[]>([]);
  const [sampleSectionName, setSampleSectionName] = useState("");
  // Event template state
  const [eventTemplates, setEventTemplates] = useState<Phase[]>([]);
  const [eventSectionName, setEventSectionName] = useState("");
  const [eventPhaseItems, setEventPhaseItems] = useState<Record<string, ChecklistItem[]>>({});
  const [showEventSection, setShowEventSection] = useState(false);
  const [eventSectionCollapsed, setEventSectionCollapsed] = useState(false);
  // Organized event templates data - each template as separate section
  const [eventTemplatesData, setEventTemplatesData] = useState<{
    id: string;
    name: string;
    phases: Phase[];
    items: Record<string, ChecklistItem[]>;
    targetDate: Date | null;
    countdownEnabled: boolean;
  }[]>([]);
  const [eventTemplatesCollapsed, setEventTemplatesCollapsed] = useState<Record<string, boolean>>({});
  // Phase items for preview cards
  const [phaseItems, setPhaseItems] = useState<Record<string, ChecklistItem[]>>({});
  const [samplePhaseItems, setSamplePhaseItems] = useState<Record<string, ChecklistItem[]>>({});
  // Unified page loading state - tracks when all content is ready
  const [isPageFullyLoaded, setIsPageFullyLoaded] = useState(false);
  // Sharing state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedSectionForShare, setSelectedSectionForShare] = useState<{
    section: any;
    phases: any[];
    items: any[];
  } | null>(null);
  const { toast, dismiss: dismissToast } = useToast();
  const { user } = useAuth();
  
  const { isAdmin, isLoading } = useAdminAuth();
  const { templates: preloadedTemplates, userSections: preloadedUserSections, isLoading: isDataLoading, hasLoadedUserSections, refetchUserSections } = useDataPreloader(user?.id);
  const { getSectionData } = useSharing();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Always use root path as canonical
  const canonicalUrl = '/';
  const sampleAndTemplatesRef = useRef<HTMLDivElement>(null);
  const myEventsHeaderRef = useRef<HTMLDivElement>(null);
  const createEventBoxRef = useRef<HTMLDivElement>(null);
  const firstUserSectionRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const mainContentRef = useRef<HTMLElement>(null);
  const hasLoadedSampleData = useRef(false);

  // Computed state to determine when all data transformations are complete
  const isDataFullyProcessed = useMemo(() => {
    // Base requirement: preloader must be finished
    if (isDataLoading) return false;
    
    // Check if sample template data has been processed
    const hasSampleTemplate = preloadedTemplates.some(t => t.template_type === 'sample');
    const sampleItemsReady = !hasSampleTemplate || Object.keys(samplePhaseItems).length > 0;
    
    // Check if event templates (published ones) have been processed
    const hasPublishedEventTemplates = preloadedTemplates.some(
      t => t.template_type === 'event' && t.is_published
    );
    const eventTemplatesReady = !hasPublishedEventTemplates || eventTemplatesData.length > 0;
    
    // For logged-in users: wait for preloader to confirm it has loaded user sections
    // AND for sections state to be populated (if preloaded sections exist)
    const userDataReady = !user || (
      hasLoadedUserSections && (
        sections.length > 0 || preloadedUserSections.length === 0
      )
    );
    
    return sampleItemsReady && eventTemplatesReady && userDataReady;
  }, [
    isDataLoading, 
    preloadedTemplates, 
    samplePhaseItems, 
    eventTemplatesData, 
    user,
    hasLoadedUserSections,
    preloadedUserSections, 
    sections
  ]);

  // Load sample checklist data from preloaded templates
  const loadSampleData = () => {
    try {
      if (preloadedTemplates && preloadedTemplates.length > 0) {
        const sampleTemplate = preloadedTemplates.find(t => t.template_type === 'sample');
        if (!sampleTemplate) {
          setShowSampleSection(false);
          return;
        }
        const template = sampleTemplate;
        
        const formattedPhases = template.phases.map(phase => ({
          id: phase.id,
          title: phase.phase_title
        }));
        setSampleTriathlonPhases(formattedPhases);

        // Load countdown data for this template
        const countdownData = {
          targetDate: template.target_date ? new Date(template.target_date) : null,
          enabled: template.countdown_enabled || false
        };
        
        setSectionCountdowns(prev => ({
          ...prev,
          'sample-triathlon': countdownData
        }));

        // Update the section name to match what's in the template
        setSampleSectionName(template.template_name);
        
        // Extract template items directly from preloaded data - no DB call needed
        const itemsByPhase: Record<string, ChecklistItem[]> = {};
        template.phases.forEach(phase => {
          itemsByPhase[phase.id] = phase.items.map(item => ({
            id: item.id,
            item: item.item_text,
            comment: item.notes || ''
          }));
        });
        setSamplePhaseItems(itemsByPhase);
      } else {
        // If no template data, hide the sample section
        setShowSampleSection(false);
      }
    } catch (error) {
      secureLog.error('Error loading sample checklist data', error);
      setShowSampleSection(false);
    }
  };

  // Load event template data from preloaded templates
  const loadEventTemplates = () => {
    try {
      if (preloadedTemplates && preloadedTemplates.length > 0) {
        const eventTemplates = preloadedTemplates.filter(t => t.template_type === 'event' && t.is_published);
        
        if (eventTemplates.length === 0) {
          setShowEventSection(false);
          setEventTemplatesData([]);
          return;
        }

        // Organize each template as a separate section
        const organizedTemplates = eventTemplates.map((template) => {
          // Format phases for this template
          const formattedPhases = template.phases.map(phase => ({
            id: phase.id,
            title: phase.phase_title
          }));

          // Extract items directly from preloaded template data - no DB call needed
          const itemsByPhase: Record<string, ChecklistItem[]> = {};
          template.phases.forEach(phase => {
            itemsByPhase[phase.id] = phase.items.map(item => ({
              id: item.id,
              item: item.item_text,
              comment: item.notes || ''
            }));
          });

          return {
            id: template.id,
            name: template.template_name,
            phases: formattedPhases,
            items: itemsByPhase,
            targetDate: template.target_date ? new Date(template.target_date) : null,
            countdownEnabled: template.countdown_enabled || false
          };
        });

        setEventTemplatesData(organizedTemplates);
        
        // Set all event templates to collapsed by default
        const initialCollapsedState = organizedTemplates.reduce((acc, template) => {
          acc[template.id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        setEventTemplatesCollapsed(initialCollapsedState);
        
        setShowEventSection(true);
      } else {
        setShowEventSection(false);
      }
    } catch (error) {
      setShowEventSection(false);
    }
  };

  useEffect(() => {
    // Only load sample and event data once when templates are available
    if (preloadedTemplates && preloadedTemplates.length > 0 && !hasLoadedSampleData.current) {
      hasLoadedSampleData.current = true;
      loadSampleData();
      loadEventTemplates();
    }
    
    // Listen for admin data changes
    const handleAdminDataChange = () => {
      hasLoadedSampleData.current = false;
      setIsPageFullyLoaded(false);
      loadSampleData();
      loadEventTemplates();
    };
    
    window.addEventListener('adminDataChanged', handleAdminDataChange);
    return () => window.removeEventListener('adminDataChanged', handleAdminDataChange);
  }, [preloadedTemplates]);

  // Auto-collapse Samples & Templates for logged-in users with existing sections
  useEffect(() => {
    // If user just logged in and has their own sections, auto-collapse the samples
    if (user && sections.length > 0) {
      const savedPreference = localStorage.getItem('samples-templates-hidden');
      if (savedPreference === null) {
        // User hasn't set a preference, auto-collapse since they have content
        setShowSamplesAndTemplates(false);
      }
    }
  }, [user, sections.length]);

  useEffect(() => {
    // Load countdown data when admin auth state changes
    if (isAdmin) {
      loadCountdownData();
      loadSampleData(); // Reload sample checklist data when admin logs in
      loadEventTemplates(); // Reload event templates when admin logs in
    }
  }, [isAdmin]);

  // Reusable function to refetch user data from database (for manual refresh)
  const refetchUserData = async () => {
    if (!user) return;
    
    try {
      const { data: userSections, error } = await supabase
        .from('checklist_sections')
        .select('*')
        .eq('user_id', user.id)
        .order('display_order');

      if (error) throw error;

      if (userSections && userSections.length > 0) {
        // Load phases for each section
        const sectionsWithPhases = await Promise.all(
          userSections.map(async (section) => {
            const { data: phases, error: phasesError } = await supabase
              .from('checklist_phases')
              .select('*')
              .eq('section_id', section.id)
              .eq('user_id', user.id)
              .order('display_order');

            if (phasesError) {
              secureLog.error('Error loading phases for section', { sectionId: section.id, error: phasesError });
              return {
                id: section.id,
                name: section.section_name,
                phases: [{ id: `placeholder-${section.id}`, title: 'Add Checklist', isPlaceholder: true }],
                targetDate: section.target_date ? new Date(section.target_date) : null,
                countdownEnabled: section.countdown_enabled,
              };
            }

            const formattedPhases: Phase[] = phases ? phases.map(phase => ({
              id: phase.id,
              title: phase.phase_title
            })) : [];

            // Add placeholder phase
            formattedPhases.push({ id: `placeholder-${section.id}`, title: 'Add Checklist', isPlaceholder: true });

            return {
              id: section.id,
              name: section.section_name,
              phases: formattedPhases,
              targetDate: section.target_date ? new Date(section.target_date) : null,
              countdownEnabled: section.countdown_enabled,
            };
          })
        );

        setSections(sectionsWithPhases);
        
        // Load checklist items for all phases
        const allPhaseIds = sectionsWithPhases.flatMap(section => 
          section.phases.filter(p => !p.isPlaceholder).map(p => p.id)
        );
        
        if (allPhaseIds.length > 0) {
          const { data: allItems, error: itemsError } = await supabase
            .from('checklist_items')
            .select('*')
            .in('phase_id', allPhaseIds)
            .eq('user_id', user.id)
            .order('phase_id, display_order');
          
          if (!itemsError && allItems) {
            const itemsByPhase: Record<string, ChecklistItem[]> = {};
            allItems.forEach(item => {
              if (!itemsByPhase[item.phase_id]) {
                itemsByPhase[item.phase_id] = [];
              }
              itemsByPhase[item.phase_id].push({
                id: item.id,
                item: item.item_text,
                comment: item.notes || ''
              });
            });
            setPhaseItems(itemsByPhase);
          }
        }
        
        // Populate sectionCountdowns with the loaded countdown data
        const countdowns: Record<string, { targetDate: Date | null; enabled: boolean }> = {};
        sectionsWithPhases.forEach(section => {
          countdowns[section.id] = {
            targetDate: section.targetDate,
            enabled: section.countdownEnabled || false
          };
        });
        setSectionCountdowns(prev => ({
          ...prev,
          ...countdowns
        }));
      } else {
        setSections([]);
      }
    } catch (error) {
      secureLog.error('Error loading user sections', error);
      setSections([]);
      toast({
        title: "Error loading data",
        description: "Failed to load your checklists. Please refresh the page.",
        variant: "destructive",
      });
    } finally {
      // Loading state managed by useDataPreloader
    }
  };

  // Load user sections and phases from Supabase database or create default sections
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        // Load guest section from sessionStorage for unauthenticated users
        const guestSection = loadGuestSection();
        const guestPhases = loadGuestPhases();
        
        if (guestSection && guestPhases.length > 0) {
          const targetDate = guestSection.targetDate ? new Date(guestSection.targetDate) : null;
          const countdownEnabled = guestSection.countdownEnabled || false;
          
          setSections([{
            id: guestSection.id,
            name: guestSection.name,
            phases: [
              ...guestPhases.map(p => ({ id: p.id, title: p.title })),
              { id: `placeholder-${guestSection.id}`, title: 'Add Checklist', isPlaceholder: true }
            ],
            targetDate,
            countdownEnabled
          }]);
          
          // Populate sectionCountdowns for guest user
          setSectionCountdowns(prev => ({
            ...prev,
            [guestSection.id]: { targetDate, enabled: countdownEnabled }
          }));
          
          // Load guest items into phaseItems state
          const itemsByPhase: Record<string, ChecklistItem[]> = {};
          guestPhases.forEach(phase => {
            const savedItems = sessionStorage.getItem(`checklist-items-${phase.id}`);
            if (savedItems) {
              try {
                const parsedItems = JSON.parse(savedItems);
                itemsByPhase[phase.id] = parsedItems
                  .filter((item: any) => item.item && item.item.trim() !== '')
                  .map((item: any) => ({
                    id: item.id,
                    item: item.item,
                    comment: item.comment || ''
                  }));
              } catch (error) {
                secureLog.error('Error parsing guest items', error);
              }
            }
          });
          setPhaseItems(itemsByPhase);
        } else {
          setSections([]);
        }
        return;
      }

  // User is authenticated - check if we need to auto-save guest data first
  const guestSection = loadGuestSection();
  if (guestSection && !sessionStorage.getItem('guest-save-in-progress')) {
    sessionStorage.setItem('guest-save-in-progress', 'true');
    try {
      await autoSaveGuestChecklist(user.id);
    } finally {
      sessionStorage.removeItem('guest-save-in-progress');
    }
    return; // Data will be loaded by useDataPreloader hook
  }

      // Data is loaded by useDataPreloader hook, no need to call loadUserData here
    };

    loadData();
  }, [user]);

  // Use preloaded user sections from useDataPreloader hook
  useEffect(() => {
    if (!user || isDataLoading || !preloadedUserSections) return;

    try {
      if (preloadedUserSections.length > 0) {
        // Transform preloaded sections directly - no database calls needed
        const sectionsWithPhases = preloadedUserSections.map((section) => {
          const formattedPhases: Phase[] = section.phases.map(phase => ({
            id: phase.id,
            title: phase.phase_title
          }));

          // Add placeholder phase
          formattedPhases.push({ id: `placeholder-${section.id}`, title: 'Add Checklist', isPlaceholder: true });

          return {
            id: section.id,
            name: section.section_name,
            phases: formattedPhases,
            targetDate: section.target_date ? new Date(section.target_date) : null,
            countdownEnabled: section.countdown_enabled,
          };
        });

        setSections(sectionsWithPhases);
        
        // Extract items from preloaded data - no database calls needed
        const itemsByPhase: Record<string, ChecklistItem[]> = {};
        preloadedUserSections.forEach(section => {
          section.phases.forEach(phase => {
            itemsByPhase[phase.id] = phase.items.map(item => ({
              id: item.id,
              item: item.item_text,
              comment: item.notes || ''
            }));
          });
        });
        setPhaseItems(itemsByPhase);
        
        // Populate sectionCountdowns with the loaded countdown data
        const countdowns: Record<string, { targetDate: Date | null; enabled: boolean }> = {};
        sectionsWithPhases.forEach(section => {
          countdowns[section.id] = {
            targetDate: section.targetDate,
            enabled: section.countdownEnabled || false
          };
        });
        setSectionCountdowns(prev => ({
          ...prev,
          ...countdowns
        }));
      } else {
        setSections([]);
      }
    } catch (error) {
      secureLog.error('Error loading sections from preloaded data', error);
      setSections([]);
    }
  }, [user, isDataLoading, preloadedUserSections]);

  // Mark page as fully loaded when all data is processed
  useEffect(() => {
    if (isDataFullyProcessed) {
      // Use requestAnimationFrame to ensure React has painted before showing content
      requestAnimationFrame(() => {
        setIsPageFullyLoaded(true);
      });
    }
  }, [isDataFullyProcessed]);

  // Failsafe: Force page to load after 5 seconds if still stuck
  useEffect(() => {
    const failsafe = setTimeout(() => {
      if (!isPageFullyLoaded) {
        setIsPageFullyLoaded(true);
      }
    }, 5000);
    return () => clearTimeout(failsafe);
  }, [isPageFullyLoaded]);

  useEffect(() => {
    // Load UI preferences only from localStorage (not checklist data)
    const savedShowIronman = localStorage.getItem('show-ironman-section');
    if (savedShowIronman !== null) {
      setShowSampleSection(JSON.parse(savedShowIronman));
    }
    
    const savedIronmanCollapsed = localStorage.getItem('ironman-section-collapsed');
    if (savedIronmanCollapsed !== null) {
      setSampleSectionCollapsed(JSON.parse(savedIronmanCollapsed));
    }

    const savedCombinedCollapsed = localStorage.getItem('combined-section-collapsed');
    if (savedCombinedCollapsed !== null) {
      setCombinedSectionCollapsed(JSON.parse(savedCombinedCollapsed));
    }

    // Load user section collapsed states
    const savedUserCollapsed = localStorage.getItem('user-sections-collapsed');
    if (savedUserCollapsed !== null) {
      setUserSectionCollapsed(JSON.parse(savedUserCollapsed));
    }
  }, []);

  // Add beforeunload warning for guest users
  useEffect(() => {
    if (!user && sections.length > 0) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = 'You have an unsaved checklist. Sign up to save your work.';
        return e.returnValue;
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [user, sections]);

  // Handle hash-based navigation for "Create Your Checklist"
  useEffect(() => {
    if (location.hash !== '#create-checklist') return;
    if (!isPageFullyLoaded) return; // Wait for page to be fully loaded
    
    const scrollToUserSection = () => {
      if (myEventsHeaderRef.current) {
        myEventsHeaderRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        
        // Clear the hash after scrolling
        navigate(location.pathname, { replace: true });
      }
    };
    
    // Small delay for render stability after page loads
    setTimeout(scrollToUserSection, 100);
  }, [location.hash, isPageFullyLoaded, navigate, location.pathname]);

  // Handle hash-based navigation for "Sample Checklists" from /start page
  useEffect(() => {
    if (location.hash !== '#sample-checklists') return;
    if (!isPageFullyLoaded) return; // Wait for page to be fully loaded
    
    const scrollToSample = () => {
      // Show the section if hidden
      setShowSamplesAndTemplates(true);
      
      // Expand the combined section if collapsed
      if (combinedSectionCollapsed) {
        setCombinedSectionCollapsed(false);
        localStorage.setItem('combined-section-collapsed', JSON.stringify(false));
      }
      // Expand sample section if collapsed
      if (sampleSectionCollapsed) {
        setSampleSectionCollapsed(false);
        localStorage.setItem('sample-section-collapsed', JSON.stringify(false));
      }
      if (eventSectionCollapsed) {
        setEventSectionCollapsed(false);
        localStorage.setItem('event-section-collapsed', JSON.stringify(false));
      }

      // Track when users click to view sample and templates
      trackEvent('cta_view_sample_from_start_page', {
        user_authenticated: !!user
      });

      setTimeout(() => {
        if (sampleAndTemplatesRef.current) {
          sampleAndTemplatesRef.current.scrollIntoView({
            behavior: 'smooth', 
            block: 'start' 
          });
        }
        // Clear the hash after scrolling
        navigate(location.pathname, { replace: true });
      }, 100);
    };
    
    // Small delay for render stability after page loads
    setTimeout(scrollToSample, 100);
  }, [location.hash, isPageFullyLoaded, navigate, location.pathname, combinedSectionCollapsed, sampleSectionCollapsed, eventSectionCollapsed, user]);

  // Handle query param-based navigation for "Getting Started" onboarding modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('onboarding') !== 'true') return;
    if (!isPageFullyLoaded) return; // Wait for page to be fully loaded
    
    // Open the onboarding modal
    setIsOnboardingOpen(true);
    
    // Clear the query param after opening
    navigate(location.pathname, { replace: true });
  }, [location.search, isPageFullyLoaded, navigate, location.pathname]);

  // Handle scrolling to newly created section once ref is available with retry logic
  useEffect(() => {
    if (!newlyCreatedSectionId) return;
    
    const attemptScroll = (attempt = 0) => {
      const element = sectionRefs.current[newlyCreatedSectionId];
      
      if (element) {
        // Scroll to center the newly created section in the viewport
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Clear the ID after scrolling
        setTimeout(() => setNewlyCreatedSectionId(null), 500);
      } else if (attempt < 5) {
        // Retry up to 5 times with increasing delay
        setTimeout(() => attemptScroll(attempt + 1), 100 * (attempt + 1));
      } else {
        setNewlyCreatedSectionId(null);
      }
    };
    
    attemptScroll();
  }, [newlyCreatedSectionId, sections]);

  const fetchUserChecklistData = async (phaseId: string) => {
    secureLog.debug('fetchUserChecklistData called', { phaseId });
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      secureLog.debug('Current authenticated user available for operation');
      
      if (!user) {
        secureLog.warn('No authenticated user found');
        return [];
      }

      secureLog.debug('Querying checklist_items', { phaseId, userId: user.id });
      
      const { data: items, error } = await supabase
        .from('checklist_items')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('user_id', user.id)
        .order('display_order');

      secureLog.debug('Database query result', { itemCount: items?.length || 0 });
      
      if (error) {
        secureLog.error('Database error occurred', error);
        throw error;
      }

      if (items && items.length > 0) {
        secureLog.debug('Found checklist items, returning mapped data');
        const mappedItems = items.map(item => ({
          id: item.id,
          target: item.is_secured,
          ready: item.is_ready,
          insert: item.is_bundled,
          item: item.item_text,
          comment: item.notes || ''
        }));
        secureLog.debug('Mapped items successfully', { count: mappedItems.length });
        return mappedItems;
      } else {
        secureLog.debug('No items found in database, returning default empty rows');
        
        // Return default 5 empty rows if no items exist
        return Array.from({ length: 5 }, (_, index) => ({
          id: crypto.randomUUID(),
          target: false,
          ready: false,
          insert: false,
          item: '',
          comment: ''
        }));
      }
    } catch (error) {
      secureLog.error('Error loading user checklist items', error);
      secureLog.debug('Returning default empty rows due to error');
      
      // Return default 5 empty rows on error
      return Array.from({ length: 5 }, (_, index) => ({
        id: crypto.randomUUID(),
        target: false,
        ready: false,
        insert: false,
        item: '',
        comment: ''
      }));
    }
  };

  const fetchTemplateData = async (phaseId: string) => {
    try {
      const { data: items, error } = await supabase
        .from('template_items')
        .select('*')
        .eq('phase_id', phaseId)
        .order('display_order');

      if (error) throw error;

      if (items && items.length > 0) {
        return items.map(item => ({
          id: item.id,
          target: item.is_secured,
          ready: item.is_ready,
          insert: item.is_bundled,
          item: item.item_text,
          comment: item.notes || '',
          url: item.ads_url || undefined
        }));
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  };

  const handlePhaseSelect = useCallback(async (phaseId: string, phaseTitle: string, isTemplate?: boolean) => {
    // Check if this is a template phase (prioritize explicit parameter, then check sample phases)
    const isSamplePhase = isTemplate ?? (
      sampleTriathlonPhases.some(phase => phase.id === phaseId) ||
      eventTemplatesData.some(template => template.phases.some(phase => phase.id === phaseId))
    );
    
    // Check if this is from default section and user is not logged in
    const isDefaultSection = sections.some(section => 
      section.id === 'default-section' && section.phases.some(phase => phase.id === phaseId)
    );
    
    if (!user && isDefaultSection && !isSamplePhase) {
      toast({
        title: "Login required",
        description: "You must be logged in to open checklists.",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedPhase(phaseId);
    setSelectedPhaseTitle(phaseTitle);
    setIsModalDataLoading(true);

    trackEvent('checklist_modal_opened', {
      phase_id: phaseId,
      // phase_title removed for privacy - don't send user's personal checklist names to analytics
      is_template: isSamplePhase,
      user_authenticated: !!user
    });
    
    // Track specifically when viewing sample checklist
    if (isSamplePhase) {
      trackEvent('cta_view_sample_clicked', {
        phase_id: phaseId,
        phase_title: phaseTitle,
        user_authenticated: !!user
      });
    }

    try {
      let data;
      if (isSamplePhase) {
        data = await fetchTemplateData(phaseId);
      } else {
        data = await fetchUserChecklistData(phaseId);
      }
      
      setPreloadedModalData(data);
      setIsModalOpen(true);
    } catch (error) {
      setPreloadedModalData([]);
      setIsModalOpen(true);
    } finally {
      setIsModalDataLoading(false);
    }
  }, [user, sections, sampleTriathlonPhases, eventTemplatesData, toast, trackEvent, fetchUserChecklistData, fetchTemplateData, setSelectedPhase, setSelectedPhaseTitle, setIsModalDataLoading, setPreloadedModalData, setIsModalOpen]);

  const toggleSampleSectionCollapse = useCallback(() => {
    const newValue = !sampleSectionCollapsed;
    setSampleSectionCollapsed(newValue);
    localStorage.setItem('sample-section-collapsed', JSON.stringify(newValue));
  }, [sampleSectionCollapsed]);

  const toggleEventSectionCollapse = () => {
    const newValue = !eventSectionCollapsed;
    setEventSectionCollapsed(newValue);
    localStorage.setItem('event-section-collapsed', JSON.stringify(newValue));
  };

  const toggleCombinedSectionCollapse = useCallback(() => {
    const newValue = !combinedSectionCollapsed;
    setCombinedSectionCollapsed(newValue);
    localStorage.setItem('combined-section-collapsed', JSON.stringify(newValue));
  }, [combinedSectionCollapsed]);

  const toggleSamplesAndTemplates = useCallback(() => {
    setShowSamplesAndTemplates(prev => {
      const newState = !prev;
      // Save user's explicit preference
      localStorage.setItem('samples-templates-hidden', JSON.stringify(!newState));
      
      if (newState) {
        // When showing, expand sample and collapse all templates
        setSampleSectionCollapsed(false);
        const resetTemplates: Record<string, boolean> = {};
        eventTemplatesData.forEach(template => {
          resetTemplates[template.id] = true;
        });
        setEventTemplatesCollapsed(resetTemplates);
      }
      return newState;
    });
  }, [eventTemplatesData]);

  const toggleEventTemplateCollapse = useCallback((templateId: string) => {
    setEventTemplatesCollapsed(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  }, []);

  const updatePhaseTitle = async (phaseId: string, newTitle: string) => {
    if (!user) {
      // Guest mode: update sessionStorage
      const guestPhases = loadGuestPhases();
      if (guestPhases && guestPhases.length > 0) {
        const updatedPhases = guestPhases.map(p => 
          p.id === phaseId ? { ...p, title: newTitle } : p
        );
        saveGuestPhases(updatedPhases);
        
        // Update local state
        setSections(prev => prev.map(section => ({
          ...section,
          phases: section.phases.map(phase =>
            phase.id === phaseId ? { ...phase, title: newTitle } : phase
          )
        })));
        
        toast({
          title: "Checklist renamed",
          description: `Checklist renamed to "${newTitle}".`,
        });
      }
      return;
    }

    // Find the section this phase belongs to
    const parentSection = sections.find(section => 
      section.phases.some(phase => phase.id === phaseId)
    );
    
    if (parentSection) {
      // Check for duplicate tile names within the same section
      const existingTitles = parentSection.phases
        .filter(phase => phase.id !== phaseId && !phase.isPlaceholder)
        .map(phase => phase.title.toLowerCase());
      
      if (existingTitles.includes(newTitle.toLowerCase())) {
        toast({
          title: "Duplicate checklist name",
          description: "A checklist with this name already exists in this section. Please choose a different name.",
          variant: "destructive"
        });
        return;
      }
    }
    
    try {
      const { error } = await supabase
        .from('checklist_phases')
        .update({ phase_title: newTitle })
        .eq('id', phaseId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      const updatedSections = sections.map(section => ({
        ...section,
        phases: section.phases.map(phase =>
          phase.id === phaseId ? { ...phase, title: newTitle } : phase
        )
      }));
      setSections(updatedSections);

      toast({
        title: "Checklist renamed",
        description: `Checklist renamed to "${newTitle}".`,
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Failed to update checklist title. Please try again.",
        variant: "destructive"
      });
    }
  };

  const autoSaveGuestChecklist = async (userId: string) => {
    const guestSection = loadGuestSection();
    const guestPhases = loadGuestPhases();
    
    if (!guestSection || guestPhases.length === 0) {
      return; // No guest data to save
    }
    
    try {
      // Check if user already has existing sections
      // Check if user has reached the maximum of 10 sections
      const { count, error: countError } = await supabase
        .from('checklist_sections')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      if (countError) throw countError;
      
      if (count !== null && count >= 10) {
        clearGuestData();
        toast({
          title: "Section limit reached",
          description: "Your account has reached the maximum of 10 checklist sections. We couldn't save your guest checklist, but you can delete an existing section and manually recreate it.",
          variant: "destructive"
        });
        return;
      }
      
      // Get current max display_order
      const { data: maxOrderData } = await supabase
        .from('checklist_sections')
        .select('display_order')
        .eq('user_id', userId)
        .order('display_order', { ascending: false })
        .limit(1);
      
      const newDisplayOrder = maxOrderData && maxOrderData.length > 0 
        ? maxOrderData[0].display_order + 1 
        : 0;
      
      // Insert section
      const { data: newSection, error: sectionError } = await supabase
        .from('checklist_sections')
        .insert({
          user_id: userId,
          section_name: guestSection.name,
          target_date: guestSection.targetDate,
          countdown_enabled: guestSection.countdownEnabled,
          display_order: newDisplayOrder,
          section_type: 'user'
        })
        .select()
        .single();
      
      if (sectionError) throw sectionError;
      
      // Insert phases and items
      for (let i = 0; i < guestPhases.length; i++) {
        const phase = guestPhases[i];
        const { data: newPhase, error: phaseError } = await supabase
          .from('checklist_phases')
          .insert({
            user_id: userId,
            section_id: newSection.id,
            phase_title: phase.title,
            display_order: i
          })
          .select()
          .single();
        
        if (phaseError) throw phaseError;
        
        // Load and insert checklist items from sessionStorage
        const itemsKey = `checklist-items-${phase.id}`;
        const itemsData = sessionStorage.getItem(itemsKey);
        
        if (itemsData) {
          const items = JSON.parse(itemsData);
          const itemsToInsert = items.map((item: any, index: number) => ({
            user_id: userId,
            phase_id: newPhase.id,
            section_id: newSection.id,
            item_text: item.item || '',
            notes: item.comment || null,
            is_secured: item.target || false,
            is_bundled: item.insert || false,
            is_ready: item.ready || false,
            display_order: index
          }));
          
          if (itemsToInsert.length > 0) {
            const { error: itemsError } = await supabase
              .from('checklist_items')
              .insert(itemsToInsert);
            
            if (itemsError) throw itemsError;
          }
        }
      }
      
      // Clear guest data
      clearGuestData();
      
      toast({
        title: "Checklist saved!",
        description: `Your checklist "${guestSection.name}" has been saved.`,
      });
      
      // Reload all user data to display the saved checklist
      await refetchUserData();
      
    } catch (error) {
      toast({
        title: "Save failed",
        description: "We couldn't save your guest checklist. Please try again or contact support.",
        variant: "destructive"
      });
    }
  };

  const addSection = async () => {
    if (!user) {
      // Guest mode: only allow 1 section
      if (sections.length >= 1) {
        toast({
          title: "Sign up to add more sections",
          description: "Create a free account to manage multiple event checklists!",
          variant: "default",
          action: (
            <Button 
              variant="accent" 
              size="sm" 
              onPointerDown={(e) => {
                e.stopPropagation();
                dismissToast();
                navigate('/auth?tab=signup');
              }}
              className="font-normal"
              aria-label="Sign up for a free account"
            >
              Sign Up
            </Button>
          ),
        });
        return;
      }
      
      // Create guest section
      const guestSectionId = 'guest-section-' + crypto.randomUUID();
      const guestPhaseId = 'guest-checklist-' + crypto.randomUUID();
      
      const newGuestSection: GuestSection = {
        id: guestSectionId,
        name: 'My Event',
        targetDate: null,
        countdownEnabled: false
      };
      
      const newGuestPhase: GuestPhase = {
        id: guestPhaseId,
        title: 'Checklist 1'
      };
      
      saveGuestSection(newGuestSection);
      saveGuestPhases([newGuestPhase]);
      
      // Update UI
      const newSection = {
        id: guestSectionId,
        name: 'My Event',
        phases: [
          { id: guestPhaseId, title: 'Checklist 1' },
          { id: `placeholder-${guestSectionId}`, title: 'Add Checklist', isPlaceholder: true }
        ],
        targetDate: null,
        countdownEnabled: false
      };
      
      setSections([newSection]);
      
      // Track the newly created section for scrolling
      setNewlyCreatedSectionId(guestSectionId);
      
      toast({
        title: "Checklist created!",
        description: "Remember to sign up to save your work.",
        action: (
          <Button 
            variant="accent" 
            size="sm" 
            onPointerDown={(e) => {
              e.stopPropagation();
              dismissToast();
              navigate('/auth?tab=signup');
            }}
            className="font-normal"
            aria-label="Sign up for a free account"
          >
            Sign Up
          </Button>
        ),
      });
      
      // Track guest section creation
      trackEvent('section_created', {
        user_authenticated: false,
        is_first_section: true
      });
      
      return;
    }
    
    if (sections.length >= 10) return;
    
    // Generate unique section name
    let baseName = "My Event";
    let counter = 1;
    let newName = baseName;
    
    while (sections.some(section => section.name.toLowerCase() === newName.toLowerCase())) {
      counter++;
      newName = `${baseName} ${counter}`;
    }
    
    try {
      // Insert new section into database with proper display_order
      const { data: newSection, error: sectionError } = await supabase
        .from('checklist_sections')
        .insert({
          user_id: user.id,
          section_name: newName,
          display_order: await calculateUserSectionDisplayOrder(user.id),
          section_type: 'user'
        })
        .select()
        .single();

      if (sectionError) throw sectionError;

      // Insert default first phase
      const { data: newPhase, error: phaseError } = await supabase
        .from('checklist_phases')
        .insert({
          section_id: newSection.id,
          user_id: user.id,
          phase_title: 'Checklist 1',
          display_order: 0
        })
        .select()
        .single();

      if (phaseError) throw phaseError;

      // Update local state
      const formattedSection: Section = {
        id: newSection.id,
        name: newSection.section_name,
        phases: [
          { id: newPhase.id, title: newPhase.phase_title },
          { id: `placeholder-${newSection.id}`, title: 'Add Checklist', isPlaceholder: true }
        ]
      };
      
      const updatedSections = [...sections, formattedSection];
      setSections(updatedSections);

      toast({
        title: "Event added",
        description: `Created new event "${newName}".`,
      });
      
      // Track the newly created section for scrolling
      setNewlyCreatedSectionId(newSection.id);
      
      // Track authenticated section creation
      trackEvent('section_created', {
        user_authenticated: true,
        total_sections: updatedSections.length
      });
    } catch (error) {
      toast({
        title: "Failed to create section",
        description: "There was an error creating the section. Please try again.",
        variant: "destructive"
      });
    }
  };

  const updateSectionName = useCallback(async (sectionId: string, newName: string) => {
    if (!user) {
      // Guest mode: update sessionStorage
      const guestSection = loadGuestSection();
      if (guestSection && guestSection.id === sectionId) {
        const trimmedName = newName.slice(0, 50);
        const updatedSection = { ...guestSection, name: trimmedName };
        saveGuestSection(updatedSection);
        
        // Update UI
        setSections(prev => prev.map(s => 
          s.id === sectionId ? { ...s, name: trimmedName } : s
        ));
        
        toast({
          title: "Section renamed",
          description: `Section renamed to "${trimmedName}".`,
        });
      }
      return;
    }

    // Enforce character limit
    const trimmedName = newName.slice(0, 50);
    
    // Check for duplicate section names (case insensitive)
    const existingNames = sections
      .filter(section => section.id !== sectionId)
      .map(section => section.name.toLowerCase());
    
    if (existingNames.includes(trimmedName.toLowerCase())) {
      toast({
        title: "Duplicate event name",
        description: "An event with this name already exists. Please choose a different name.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('checklist_sections')
        .update({ section_name: trimmedName })
        .eq('id', sectionId)
        .eq('user_id', user.id);

      if (error) throw error;

      const updatedSections = sections.map(section =>
        section.id === sectionId ? { ...section, name: trimmedName } : section
      );
      setSections(updatedSections);

      toast({
        title: "Section renamed",
        description: `Section renamed to "${trimmedName}".`,
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Failed to update event name. Please try again.",
        variant: "destructive"
      });
    }
  }, [user, sections, toast]);

  const handleCountdownUpdate = useCallback(async (sectionId: string, targetDate: Date | null, enabled: boolean) => {
    if (!user) {
      // Guest mode: update guest section in sessionStorage
      const guestSection = loadGuestSection();
      if (guestSection) {
        const updatedSection: GuestSection = {
          ...guestSection,
          targetDate: targetDate?.toISOString() || null,
          countdownEnabled: enabled
        };
        saveGuestSection(updatedSection);
      }
      
      setSectionCountdowns(prev => ({
        ...prev,
        [sectionId]: { targetDate, enabled }
      }));
      
      toast({
        title: enabled ? "Countdown set" : "Countdown removed",
        description: enabled && targetDate ? `Event date set for ${format(targetDate, 'PPP')}` : "Countdown removed",
      });
      return;
    }

    // Update local state immediately
    setSectionCountdowns(prev => ({
      ...prev,
      [sectionId]: { targetDate, enabled }
    }));

    // For admin sample checklist, save to database using templates
    if (sectionId === 'sample-triathlon') {
      try {
        const { data: templates, error } = await supabase
          .from('templates')
          .select('id')
          .eq('is_active', true)
          .order('display_order')
          .limit(1);

        if (templates && templates.length > 0) {
          const { error: updateError } = await supabase
            .from('templates')
            .update({
              target_date: targetDate ? targetDate.toISOString().split('T')[0] : null,
              countdown_enabled: enabled
            })
            .eq('id', templates[0].id);

          if (updateError) throw updateError;
          
          toast({
            title: "Sample checklist countdown updated",
            description: targetDate ? `Event date set for ${format(targetDate, 'PPP')}` : "Countdown removed",
          });
          
          // Reload the sample and event template data to sync changes
          loadSampleData();
          loadEventTemplates();
          return;
        }
      } catch (error) {
        toast({
          title: "Error updating countdown",
          description: "Failed to update sample checklist countdown",
          variant: "destructive"
        });
        return;
      }
    }

    // For authenticated user sections, save to Supabase database
    try {
      const { error } = await supabase
        .from('checklist_sections')
        .update({
          target_date: targetDate ? targetDate.toISOString().split('T')[0] : null,
          countdown_enabled: enabled
        })
        .eq('id', sectionId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      toast({
        title: "Countdown updated",
        description: targetDate ? `Event date set for ${format(targetDate, 'PPP')}` : "Countdown removed",
      });
    } catch (error) {
      toast({
        title: "Error updating countdown",
        description: "Failed to update countdown. Please try again.",
        variant: "destructive"
      });
      
      // Revert local state on error
      setSectionCountdowns(prev => ({
        ...prev,
        [sectionId]: { 
          targetDate: sections.find(s => s.id === sectionId)?.targetDate || null, 
          enabled: sections.find(s => s.id === sectionId)?.countdownEnabled || false 
        }
      }));
    }
  }, [user, toast, sectionCountdowns, sections]);

  const loadCountdownData = async () => {
    if (!isAdmin) return;

    // For test mode, load from localStorage instead of Supabase
    const savedCountdowns = localStorage.getItem('section-countdowns');
    if (savedCountdowns) {
      try {
        const countdownData = JSON.parse(savedCountdowns);
        const countdowns: Record<string, { targetDate: Date | null; enabled: boolean }> = {};
        
        Object.entries(countdownData).forEach(([sectionId, data]: [string, any]) => {
          countdowns[sectionId] = {
            targetDate: data.targetDate ? new Date(data.targetDate) : null,
            enabled: data.enabled || false
          };
        });
        
        setSectionCountdowns(countdowns);
      } catch (error) {
        // Silently fail - not critical for app function
      }
    }
  };

  // Helper function to calculate proper display_order for new user sections
  const calculateUserSectionDisplayOrder = async (userId: string): Promise<number> => {
    const { data } = await supabase
      .from('checklist_sections')
      .select('display_order')
      .eq('user_id', userId)
      .order('display_order', { ascending: false })
      .limit(1);
    return data && data.length > 0 ? data[0].display_order + 1 : 0;
  };


  // Helper function to determine if a section can be removed
  const canRemoveSection = useCallback((section: Section): boolean => {
    // All sections can now be removed - no forced defaults
    return true;
  }, []);

  const removeSection = useCallback(async (sectionId: string) => {
    if (!user) {
      // Guest mode: clear sessionStorage and UI
      const guestSection = loadGuestSection();
      if (guestSection && guestSection.id === sectionId) {
        clearGuestData();
        setSections([]);
        toast({
          title: "Section deleted",
          description: "Your draft checklist has been removed.",
        });
      }
      return;
    }

    try {
      // Delete items first (referential integrity)
      const { error: itemsError } = await supabase
        .from('checklist_items')
        .delete()
        .eq('section_id', sectionId)
        .eq('user_id', user?.id);

      if (itemsError) throw itemsError;

      // Delete phases second
      const { error: phasesError } = await supabase
        .from('checklist_phases')
        .delete()
        .eq('section_id', sectionId)
        .eq('user_id', user?.id);

      if (phasesError) throw phasesError;

      // Delete section last
      const { error: sectionError } = await supabase
        .from('checklist_sections')
        .delete()
        .eq('id', sectionId)
        .eq('user_id', user?.id);

      if (sectionError) throw sectionError;

      // Clean up phase titles from local state
      const sectionToRemove = sections.find(s => s.id === sectionId);
      if (sectionToRemove) {
        const updatedTitles = { ...phaseTitles };
        sectionToRemove.phases.forEach(phase => {
          delete updatedTitles[phase.id];
        });
        setPhaseTitles(updatedTitles);
      }

      const updatedSections = sections.filter(s => s.id !== sectionId);
      
      // Update sections state - allow empty sections array
      setSections(updatedSections);

      // Refresh search data
      await refetchUserSections();

      toast({
        title: "Event removed",
        description: "Event and all its checklists have been removed.",
      });

    } catch (error) {
      toast({
        title: "Remove failed",
        description: "Failed to remove event. Please try again.",
        variant: "destructive"
      });
    }
  }, [user, sections, phaseTitles, toast, refetchUserSections]);

  const addTile = useCallback(async (sectionId: string) => {
    if (!user) {
      const guestPhases = loadGuestPhases();
      
      if (guestPhases.length >= 1) {
        toast({
          title: "Sign up to add more checklists",
          description: "Create a free account to manage up to 15 checklists per section!",
          variant: "default",
          action: (
            <Button 
              variant="accent" 
              size="sm" 
              onPointerDown={(e) => {
                e.stopPropagation();
                dismissToast();
                navigate('/auth?tab=signup');
              }}
              className="font-normal"
              aria-label="Sign up for a free account"
            >
              Sign Up
            </Button>
          ),
        });
        return;
      }
      
      // Create guest phase
      const newGuestPhaseId = 'guest-checklist-' + crypto.randomUUID();
      const updatedPhases = [
        ...guestPhases,
        { id: newGuestPhaseId, title: `Checklist ${guestPhases.length + 1}` }
      ];
      
      saveGuestPhases(updatedPhases);
      
      // Update UI
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        const updatedSection = {
          ...section,
          phases: [
            ...updatedPhases.map(p => ({ id: p.id, title: p.title })),
            { id: `placeholder-${sectionId}`, title: 'Add Checklist', isPlaceholder: true }
          ]
        };
        setSections([updatedSection]);
      }
      
      // Track guest checklist creation
      trackEvent('checklist_created', {
        user_authenticated: false,
        total_checklists_in_section: updatedPhases.length
      });
      
      return;
    }
    
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const actualPhases = section.phases.filter(p => !p.isPlaceholder);
    if (actualPhases.length >= 15) return;
    
    // Generate unique checklist name within the section
    let baseName = "Checklist";
    let counter = actualPhases.length + 1;
    let newTitle = `${baseName} ${counter}`;
    
    // Check existing titles in this section to ensure uniqueness
    const existingTitles = actualPhases.map(phase => 
      (phaseTitles[phase.id] || phase.title).toLowerCase()
    );
    
    while (existingTitles.includes(newTitle.toLowerCase())) {
      counter++;
      newTitle = `${baseName} ${counter}`;
    }
    
    try {
      // Create phase in database
      const { data: newPhase, error: phaseError } = await supabase
        .from('checklist_phases')
        .insert({
          section_id: sectionId,
          user_id: user.id,
          phase_title: newTitle,
          display_order: actualPhases.length
        })
        .select()
        .single();

      if (phaseError) throw phaseError;
      
      // Update local state
      const updatedSections = sections.map(s => {
        if (s.id === sectionId) {
          const phases = s.phases.filter(p => !p.isPlaceholder);
          return {
            ...s,
            phases: [
              ...phases,
              { id: newPhase.id, title: newPhase.phase_title },
              s.phases.find(p => p.isPlaceholder)!
            ]
          };
        }
        return s;
      });
      
      setSections(updatedSections);
      
      // Track authenticated checklist creation
      trackEvent('checklist_created', {
        user_authenticated: true,
        total_checklists_in_section: actualPhases.length + 1
      });

    } catch (error) {
      toast({
        title: "Create failed",
        description: "Failed to create new checklist. Please try again.",
        variant: "destructive"
      });
    }
  }, [user, sections, phaseTitles, toast, navigate, trackEvent]);

  const removeTile = useCallback(async (sectionId: string, phaseId: string) => {
    if (!user) {
      toast({
        title: "Login required",
        description: "You must be logged in to remove checklists.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Delete phase from database (cascading deletes will handle items)
      const { error: phaseError } = await supabase
        .from('checklist_phases')
        .delete()
        .eq('id', phaseId)
        .eq('user_id', user?.id);

      if (phaseError) throw phaseError;

      // Update local state
      const updatedSections = sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            phases: s.phases.filter(p => p.id !== phaseId)
          };
        }
        return s;
      });
      
      setSections(updatedSections);
      
      // Clean up phase titles from local state
      const updatedTitles = { ...phaseTitles };
      delete updatedTitles[phaseId];
      setPhaseTitles(updatedTitles);

      toast({
        title: "Checklist removed",
        description: "Checklist and all its items have been removed.",
      });

    } catch (error) {
      toast({
        title: "Remove failed",
        description: "Failed to remove checklist. Please try again.",
        variant: "destructive"
      });
    }
  }, [user, sections, phaseTitles, toast]);

  const reorderPhases = useCallback(async (sectionId: string, reorderedPhases: Phase[]) => {
    if (!user) {
      toast({
        title: "Login required",
        description: "You must be logged in to reorder checklists.",
        variant: "destructive"
      });
      return;
    }

    // Store original state for rollback if needed
    const originalSections = [...sections];

    try {
      secureLog.debug('Starting phase reorder for section', { sectionId });
      secureLog.debug('New phase order', { reorderedPhases: reorderedPhases.map(p => ({ id: p.id, title: p.title })) });

      // Optimistically update local state first for immediate UI feedback
      const optimisticSections = sections.map(s => {
        if (s.id === sectionId) {
          const placeholder = s.phases.find(p => p.isPlaceholder);
          return {
            ...s,
            phases: placeholder ? [...reorderedPhases, placeholder] : reorderedPhases
          };
        }
        return s;
      });
      
      setSections(optimisticSections);

      // Prepare batch update data - only update phases that actually changed position
      const updates = reorderedPhases
        .map((phase, index) => ({
          id: phase.id,
          display_order: index,
          user_id: user.id // Include user_id for security
        }))
        .filter((update, index) => {
          // Find original phase position
          const originalSection = originalSections.find(s => s.id === sectionId);
          const originalPhases = originalSection?.phases.filter(p => !p.isPlaceholder) || [];
          const originalIndex = originalPhases.findIndex(p => p.id === update.id);
          // Only include if position actually changed
          return originalIndex !== index;
        });

      secureLog.debug('Phase updates prepared', { updateCount: updates.length, totalPhases: reorderedPhases.length });

      if (updates.length > 0) {
        // Use batch update with individual queries for better error handling
        secureLog.debug('Performing batch database update');
        
        for (const update of updates) {
          const { error } = await supabase
            .from('checklist_phases')
            .update({ display_order: update.display_order })
            .eq('id', update.id)
            .eq('user_id', user.id);

          if (error) {
            secureLog.error('Failed to update phase during reorder', { phaseId: update.id, error });
            throw error;
          }
        }

        secureLog.debug('Database update successful for phase reorder');
        
        // Wait a moment for database consistency, then refresh
        setTimeout(async () => {
          await refetchUserSections();
          secureLog.debug('Data refreshed after phase reorder');
        }, 100);
        
      } else {
        secureLog.debug('No position changes detected, skipping database update');
      }

      toast({
        title: "Order updated",
        description: "Checklist order has been saved.",
      });

    } catch (error) {
      secureLog.error('Error reordering phases', error);
      
      // Rollback optimistic update on error
      setSections(originalSections);
      
      toast({
        title: "Reorder failed",
        description: "Failed to reorder checklists. The order has been restored.",
        variant: "destructive"
      });
    }
  }, [user, sections, toast]);


  // Refresh phase items from database or sessionStorage
  const refreshPhaseItems = async () => {
    if (!user) {
      // Guest mode: load from sessionStorage
      const guestPhases = loadGuestPhases();
      const itemsByPhase: Record<string, ChecklistItem[]> = {};
      
      guestPhases.forEach(phase => {
        const savedItems = sessionStorage.getItem(`checklist-items-${phase.id}`);
        if (savedItems) {
          try {
            const parsedItems = JSON.parse(savedItems);
            itemsByPhase[phase.id] = parsedItems
              .filter((item: any) => item.item && item.item.trim() !== '')
              .map((item: any) => ({
                id: item.id,
                item: item.item,
                comment: item.comment || ''
              }));
          } catch (error) {
            secureLog.error('Error parsing guest items', error);
          }
        }
      });
      
      setPhaseItems(itemsByPhase);
      return;
    }

    // Authenticated user: load from database
    try {
      const allPhaseIds = sections.flatMap(section => 
        section.phases.filter(p => !p.isPlaceholder).map(p => p.id)
      );
      
      if (allPhaseIds.length > 0) {
        const { data: allItems, error: itemsError } = await supabase
          .from('checklist_items')
          .select('*')
          .in('phase_id', allPhaseIds)
          .eq('user_id', user.id)
          .order('phase_id, display_order');
        
        if (!itemsError && allItems) {
          const itemsByPhase: Record<string, ChecklistItem[]> = {};
          allItems.forEach(item => {
            if (!itemsByPhase[item.phase_id]) {
              itemsByPhase[item.phase_id] = [];
            }
            itemsByPhase[item.phase_id].push({
              id: item.id,
              item: item.item_text,
              comment: item.notes || ''
            });
          });
          setPhaseItems(itemsByPhase);
        }
      }
    } catch (error) {
      secureLog.error('Error refreshing phase items', error);
    }
  };

  const handleCloseModal = async () => {
    // IMMEDIATELY close the modal visually for instant feedback
    setIsModalOpen(false);
    setSelectedPhase(null);
    setPreloadedModalData([]);
    
    // THEN refresh data in the background (non-blocking)
    // These are for updating preview cards after save is complete
    refreshPhaseItems();
    refetchUserSections();
  };

  const safePrintText = (text: string) => {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const printSectionChecklists = useCallback(async (sectionId: string) => {
    if (!user && sectionId === 'default-section') {
      toast({
        title: "Login required",
        description: "You must be logged in to print checklists.",
        variant: "destructive"
      });
      return;
    }
    
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    const originalTitle = document.title;
    document.title = section.name;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    let allContent = '';
    
    for (const phase of section.phases.filter(p => !p.isPlaceholder)) {
      let items;
      if (!user) {
        const storageKey = `checklist-items-${phase.id}`;
        const storedData = sessionStorage.getItem(storageKey);
        items = storedData ? JSON.parse(storedData) : [];
        if (items.length === 0) {
          items = Array.from({ length: 5 }, () => ({
            id: crypto.randomUUID(),
            target: false,
            ready: false,
            insert: false,
            item: '',
            comment: ''
          }));
        }
      } else {
        items = await fetchUserChecklistData(phase.id);
      }
      
      const displayTitle = phaseTitles[phase.id] || phase.title;
      
      allContent += `
        <div class="phase-section">
          <h2>${section.name}: ${displayTitle}</h2>
          <table>
            <thead>
              <tr>
                <th class="checkbox-col">S</th>
                <th class="checkbox-col">B</th>
                <th class="checkbox-col">R</th>
                <th class="text-col">Item</th>
                <th class="text-col">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td class="checkbox-col"><input type="checkbox" class="checkbox" ${item.target ? 'checked' : ''} /></td>
                  <td class="checkbox-col"><input type="checkbox" class="checkbox" ${item.ready ? 'checked' : ''} /></td>
                  <td class="checkbox-col"><input type="checkbox" class="checkbox" ${item.insert ? 'checked' : ''} /></td>
                  <td>${safePrintText(item.item)}</td>
                  <td>${safePrintText(item.comment || '')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${document.title}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            margin: 0;
          }
          h1 { 
            color: #000000; 
            margin-bottom: 30px; 
            text-align: center; 
            font-weight: 300; 
          }
          h2 { 
            color: #000000; 
            margin: 30px 0 15px 0; 
            font-weight: 300; 
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 30px; 
          }
          th, td { 
            border: 1px solid #ccc; 
            padding: 8px; 
            text-align: left; 
            font-weight: 400;
            white-space: nowrap;
          }
          th { 
            background-color: #f5f5f5; 
            font-weight: 400; 
          }
          .checkbox { 
            width: 20px; 
            height: 20px; 
            border: 1px solid #ccc; 
            appearance: none; 
            -webkit-appearance: none; 
            -moz-appearance: none; 
            cursor: pointer; 
          }
          .checkbox:checked { 
            background-color: #ffffff; 
            position: relative; 
          }
          .checkbox:checked::after { 
            content: '✓'; 
            position: absolute; 
            color: #000000; 
            font-size: 14px; 
            font-weight: bold; 
            top: 50%; 
            left: 50%; 
            transform: translate(-50%, -50%); 
          }
          .phase-section { 
            page-break-inside: avoid; 
            margin-bottom: 40px; 
          }
          .phase-section { 
            page-break-after: always; 
          }
          .phase-section:last-child { 
            page-break-after: auto; 
          }
          .checkbox-col { 
            width: 40px; 
            text-align: center; 
          }
          .text-col {
            width: 35%;
          }
          @media print { 
            @page { 
              margin: 0.5cm; 
            }
          }
        </style>
      </head>
      <body>
        <h1>${section.name}</h1>
        ${allContent}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    // Close window after print dialog is dismissed
    printWindow.onafterprint = () => {
      printWindow.close();
      document.title = originalTitle;
    };

    setTimeout(() => {
      printWindow.print();
    }, 250);

    // Fallback: restore title if onafterprint doesn't fire
    setTimeout(() => {
      document.title = originalTitle;
    }, 5000);
  }, [user, sections, phaseTitles, toast, fetchUserChecklistData]);

  // Helper function to check if user has created checklists
  const hasUserChecklists = () => {
    // Simply check if user has any sections
    return sections.length > 0;
  };

  const toggleUserSectionCollapse = useCallback((sectionId: string) => {
    const newCollapsedStates = {
      ...userSectionCollapsed,
      [sectionId]: !userSectionCollapsed[sectionId]
    };
    setUserSectionCollapsed(newCollapsedStates);
    localStorage.setItem('user-sections-collapsed', JSON.stringify(newCollapsedStates));
  }, [userSectionCollapsed]);

  // Create stable toggle callbacks for each section to prevent re-renders
  const toggleUserSectionCallbacks = useMemo(() => {
    const callbacks: Record<string, () => void> = {};
    sections.forEach(section => {
      callbacks[section.id] = () => toggleUserSectionCollapse(section.id);
    });
    return callbacks;
  }, [sections, toggleUserSectionCollapse]);

  const expandAllUserSections = () => {
    const expandedStates: Record<string, boolean> = {};
    sections.forEach(section => {
      expandedStates[section.id] = false; // false means expanded
    });
    setUserSectionCollapsed(expandedStates);
    localStorage.setItem('user-sections-collapsed', JSON.stringify(expandedStates));
  };

  const scrollToFirstUserChecklist = () => {
    // First expand all user sections
    expandAllUserSections();
    
    // Then scroll to the My Events header
    setTimeout(() => {
      if (myEventsHeaderRef.current) {
        myEventsHeaderRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const scrollToCreateSection = () => {
    if (createEventBoxRef.current) {
      createEventBoxRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const scrollToSampleAndTemplates = () => {
    // Show the section if hidden
    setShowSamplesAndTemplates(true);
    
    // Expand the combined section if collapsed
    if (combinedSectionCollapsed) {
      setCombinedSectionCollapsed(false);
      localStorage.setItem('combined-section-collapsed', JSON.stringify(false));
    }
    // Expand sample section if collapsed
    if (sampleSectionCollapsed) {
      setSampleSectionCollapsed(false);
      localStorage.setItem('sample-section-collapsed', JSON.stringify(false));
    }
    if (eventSectionCollapsed) {
      setEventSectionCollapsed(false);
      localStorage.setItem('event-section-collapsed', JSON.stringify(false));
    }

    // Track when users click to view sample and templates
    trackEvent('cta_view_sample_and_templates_clicked', {
      user_authenticated: !!user
    });

    setTimeout(() => {
      if (sampleAndTemplatesRef.current) {
        sampleAndTemplatesRef.current.scrollIntoView({
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const copySectionChecklists = useCallback(async (sectionId: string) => {
    if (!user) {
      toast({
        title: "Sign up to copy events",
        description: "Duplicate events instantly and save time preparing for multiple events!",
        variant: "default",
        action: (
          <Button 
            variant="accent" 
            size="sm" 
            onPointerDown={(e) => {
              e.stopPropagation();
              dismissToast();
              navigate('/auth?tab=signup');
            }}
            className="font-normal"
            aria-label="Sign up for a free account"
          >
            Sign Up
          </Button>
        ),
      });
      return;
    }

    if (sections.length >= 10) {
      toast({
        title: "Maximum events reached", 
        description: "You can only have up to 10 checklist events. Please remove an existing event to add a new one.",
        variant: "destructive"
      });
      return;
    }

    const sectionToCopy = sections.find(s => s.id === sectionId);
    if (!sectionToCopy) return;

    // Show loading toast
    const loadingToast = toast({
      title: "Copying section...",
      description: "Please wait while we copy the section data.",
    });

    try {
      // Generate unique section name
      let baseName = `${sectionToCopy.name} Copy`;
      let counter = 1;
      let newName = baseName;
      
      while (sections.some(section => section.name.toLowerCase() === newName.toLowerCase())) {
        counter++;
        newName = `${baseName} ${counter}`;
      }

      // Get countdown data from the original section
      const originalCountdownData = sectionCountdowns[sectionId];
      
      // Create new section in database with proper display_order
      const { data: newSection, error: sectionError } = await supabase
        .from('checklist_sections')
        .insert({
          user_id: user.id,
          section_name: newName,
          display_order: await calculateUserSectionDisplayOrder(user.id),
          section_type: 'user',
          target_date: originalCountdownData?.targetDate ? originalCountdownData.targetDate.toISOString().split('T')[0] : null,
          countdown_enabled: originalCountdownData?.enabled || false
        })
        .select()
        .single();

      if (sectionError) throw sectionError;

      // Get phases to copy (exclude placeholders)
      const phasesToCopy = sectionToCopy.phases.filter(p => !p.isPlaceholder);
      
      // Create all phases in parallel
      const phaseInserts = phasesToCopy.map((originalPhase, index) => ({
        section_id: newSection.id,
        user_id: user.id,
        phase_title: originalPhase.title,
        display_order: index
      }));

      const { data: newPhases, error: phasesError } = await supabase
        .from('checklist_phases')
        .insert(phaseInserts)
        .select();

      if (phasesError) throw phasesError;

      // Create phase mapping for items
      const phaseMapping = new Map();
      const copiedPhases: Phase[] = newPhases.map((newPhase, index) => {
        const originalPhase = phasesToCopy[index];
        phaseMapping.set(originalPhase.id, newPhase.id);
        return {
          id: newPhase.id,
          title: newPhase.phase_title
        };
      });

      // Fetch all items for all phases in a single query
      const originalPhaseIds = phasesToCopy.map(p => p.id);
      if (originalPhaseIds.length > 0) {
        const { data: allItems, error: itemsError } = await supabase
          .from('checklist_items')
          .select('*')
          .in('phase_id', originalPhaseIds)
          .eq('user_id', user.id)
          .order('phase_id, display_order');

        if (itemsError) {
          // Skip items on error
        } else if (allItems && allItems.length > 0) {
          // Group items by phase and prepare bulk insert
          const itemsToInsert = allItems.map(item => ({
            phase_id: phaseMapping.get(item.phase_id),
            user_id: user.id,
            section_id: newSection.id,
            item_text: item.item_text,
            notes: item.notes,
            is_ready: item.is_ready,
            is_bundled: item.is_bundled,
            is_secured: item.is_secured,
            display_order: item.display_order
          }));

          // Bulk insert all items
          const { error: insertItemsError } = await supabase
            .from('checklist_items')
            .insert(itemsToInsert);

          if (insertItemsError) {
            // Items insert failed - section still created
          }
        }
      }

      // Add placeholder phase
      copiedPhases.push({ id: `placeholder-${newSection.id}`, title: 'Add Checklist', isPlaceholder: true });

      // Update local state
      const formattedSection: Section = {
        id: newSection.id,
        name: newSection.section_name,
        phases: copiedPhases
      };

      // Add copied section to the end of sections array
      const updatedSections = [...sections, formattedSection];
      
      setSections(updatedSections);

      // Copy countdown data to the new section if it exists
      if (originalCountdownData) {
        setSectionCountdowns(prev => ({
          ...prev,
          [newSection.id]: {
            targetDate: originalCountdownData.targetDate,
            enabled: originalCountdownData.enabled
          }
        }));
      }

      // Load items for the copied phases to update preview cards
      const copiedPhaseIds = copiedPhases.filter(p => !p.isPlaceholder).map(p => p.id);
      if (copiedPhaseIds.length > 0) {
        const { data: copiedItems, error: loadItemsError } = await supabase
          .from('checklist_items')
          .select('*')
          .in('phase_id', copiedPhaseIds)
          .eq('user_id', user.id)
          .order('phase_id, display_order');
        
        if (!loadItemsError && copiedItems) {
          const newItemsByPhase: Record<string, ChecklistItem[]> = {};
          copiedItems.forEach(item => {
            if (!newItemsByPhase[item.phase_id]) {
              newItemsByPhase[item.phase_id] = [];
            }
            newItemsByPhase[item.phase_id].push({
              id: item.id,
              item: item.item_text,
              comment: item.notes || ''
            });
          });
          setPhaseItems(prev => ({
            ...prev,
            ...newItemsByPhase
          }));
        }
      }

      // Dismiss loading toast
      loadingToast.dismiss();

      toast({
        title: "Section copied",
        description: `Created "${newName}" with all checklist data.`,
      });

    } catch (error) {
      
      // Dismiss loading toast
      loadingToast.dismiss();
      
      toast({
        title: "Copy failed",
        description: "There was an error copying the section. Please try again.",
        variant: "destructive"
      });
    }
  }, [user, sections, sectionCountdowns, toast, navigate]);

  // Handle section sharing
  const handleShareSection = useCallback(async (sectionId: string) => {
    if (!user) {
      toast({
        title: "Sign up to share checklists",
        description: "Share your checklists with training partners and coaches!",
        variant: "default",
        action: (
          <Button 
            variant="accent" 
            size="sm" 
            onPointerDown={(e) => {
              e.stopPropagation();
              dismissToast();
              navigate('/auth?tab=signup');
            }}
            className="font-normal"
            aria-label="Sign up for a free account"
          >
            Sign Up
          </Button>
        ),
      });
      return;
    }

    try {
      const sectionData = await getSectionData(sectionId);
      if (sectionData) {
        setSelectedSectionForShare(sectionData);
        setShareModalOpen(true);
      } else {
        toast({
          title: 'Error loading section',
          description: 'Could not load section data for sharing.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      // Errors are logged in development only via secureLog
      toast({
        title: 'Error loading section',
        description: 'Could not load section data for sharing.',
        variant: 'destructive',
      });
    }
  }, [user, toast, navigate, getSectionData, setSelectedSectionForShare, setShareModalOpen]);

  const handleClearSectionCheckboxes = useCallback(async (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    try {
      // Get all phases for this section (excluding placeholders)
      const phasesInSection = section.phases.filter(p => !p.isPlaceholder);
      const phaseIds = phasesInSection.map(p => p.id);

      if (phaseIds.length === 0) {
        toast({
          title: "No checklists to clear",
          description: "This section has no checklists.",
        });
        return;
      }

      if (!user) {
        // Guest mode: Clear checkboxes in sessionStorage
        phaseIds.forEach(phaseId => {
          const storageKey = `checklist-items-${phaseId}`;
          const itemsData = sessionStorage.getItem(storageKey);
          
          if (itemsData) {
            const items = JSON.parse(itemsData);
            const clearedItems = items.map((item: any) => ({
              ...item,
              target: false,
              ready: false,
              insert: false
            }));
            sessionStorage.setItem(storageKey, JSON.stringify(clearedItems));
          }
        });

        toast({
          title: `All checkboxes cleared in ${section.name}`,
          description: "All S, B, and R checkboxes have been cleared.",
        });
      } else {
        // Authenticated: Clear checkboxes in Supabase
        const { error } = await supabase
          .from('checklist_items')
          .update({ 
            is_secured: false, 
            is_ready: false, 
            is_bundled: false 
          })
          .in('phase_id', phaseIds)
          .eq('user_id', user.id);

        if (error) {
          // Errors are logged in development only via secureLog
          throw error;
        }

        toast({
          title: `All checkboxes cleared in ${section.name}`,
          description: "All S, B, and R checkboxes have been cleared.",
        });
      }
    } catch (error) {
      // Errors are logged in development only via secureLog
      toast({
        title: "Error",
        description: "Failed to clear checkboxes. Please try again.",
        variant: "destructive",
      });
    }
  }, [sections, user, toast]);

  const printEventTemplate = useCallback((templateId: string) => {
    const template = eventTemplatesData.find(t => t.id === templateId);
    if (!template) return;

    const originalTitle = document.title;
    document.title = `${template.name} Checklist`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      document.title = originalTitle;
      return;
    }

    const phasesHtml = template.phases
      .filter(phase => !phase.isPlaceholder)
      .map((phase) => {
        const phaseItemsList = template.items[phase.id] || [];
        const allRows = [...phaseItemsList];

        while (allRows.length < 12) {
          allRows.push({ id: `blank-${phase.id}-${allRows.length}`, item: '', comment: '' });
        }

        return `
          <div class="phase-section">
            <h2>${safePrintText(phaseTitles[phase.id] || phase.title)}</h2>
            <table>
              <thead>
                <tr>
                  <th style="width: 40px; text-align: center;">S</th>
                  <th style="width: 40px; text-align: center;">B</th>
                  <th style="width: 40px; text-align: center;">R</th>
                  <th style="width: 35%;">Item</th>
                  <th style="width: 35%;">Notes</th>
                </tr>
              </thead>
              <tbody>
                ${allRows.map((item, idx) => `
                  <tr>
                    <td style="text-align: center;"><input type="checkbox" name="target-${phase.id}-${idx}" class="checkbox" /></td>
                    <td style="text-align: center;"><input type="checkbox" name="ready-${phase.id}-${idx}" class="checkbox" /></td>
                    <td style="text-align: center;"><input type="checkbox" name="insert-${phase.id}-${idx}" class="checkbox" /></td>
                    <td style="font-weight: 400;">${safePrintText(item.item)}</td>
                    <td>${safePrintText(item.comment)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      })
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${document.title}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            margin: 0;
          }
          h1 {
            color: #000000;
            margin-bottom: 30px;
            text-align: center;
            font-weight: 300;
          }
          h2 {
            color: #000000;
            margin: 30px 0 15px 0;
            font-weight: 300;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          th, td {
            border: 1px solid #ccc;
            padding: 8px;
            text-align: left;
            font-weight: 400;
            white-space: nowrap;
          }
          th {
            background-color: #f5f5f5;
            font-weight: 400;
          }
          .checkbox {
            width: 20px;
            height: 20px;
            border: 1px solid #ccc;
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            cursor: pointer;
          }
          .checkbox:checked {
            background-color: #ffffff;
            position: relative;
          }
          .checkbox:checked::after {
            content: '✓';
            position: absolute;
            color: #000000;
            font-size: 14px;
            font-weight: bold;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
          .phase-section {
            page-break-inside: avoid;
            margin-bottom: 40px;
          }
          .phase-section {
            page-break-after: always;
          }
          .phase-section:last-child {
            page-break-after: auto;
          }
          @media print {
            @page {
              margin: 0.5cm;
            }
          }
        </style>
      </head>
      <body>
        <h1>Sink Bike Run<br/>Checklist</h1>
        <h2>${safePrintText(template.name)}</h2>
        ${phasesHtml}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => {
      printWindow.close();
      document.title = originalTitle;
    };

    setTimeout(() => {
      printWindow.print();
    }, 250);

    setTimeout(() => {
      document.title = originalTitle;
    }, 5000);
  }, [eventTemplatesData, phaseTitles]);

  const copyTemplateToUserChecklist = useCallback(async (sourceType: 'sample-triathlon' | 'event-templates' | string) => {
    if (!user) {
      toast({
        title: "Sign up to copy this checklist",
        description: "Create a free account to save and customize your own checklists!",
        variant: "default",
        action: (
          <Button 
            variant="accent" 
            size="sm" 
            onPointerDown={(e) => {
              e.stopPropagation();
              dismissToast();
              navigate('/auth?tab=signup');
            }}
            className="font-normal"
            aria-label="Sign up for a free account"
          >
            Sign Up
          </Button>
        ),
      });
      return;
    }

    // Determine which template to copy from
    let sourcePhases: Phase[];
    let sourceName: string;
    let sourceCountdownKey: string;
    let sourceTargetDate: Date | null = null;
    let sourceCountdownEnabled = false;
    
    if (sourceType === 'sample-triathlon') {
      // Copy from sample template
      sourcePhases = sampleTriathlonPhases;
      sourceName = sampleSectionName;
      sourceCountdownKey = sourceType;
      const countdownData = sectionCountdowns[sourceCountdownKey];
      sourceTargetDate = countdownData?.targetDate || null;
      sourceCountdownEnabled = countdownData?.enabled || false;
    } else {
      // Copy from a specific event template by ID
      const eventTemplate = eventTemplatesData.find(t => t.id === sourceType);
      if (!eventTemplate) {
        toast({
          title: "Error",
          description: "Template not found. Please try again.",
          variant: "destructive"
        });
        return;
      }
      sourcePhases = eventTemplate.phases;
      sourceName = eventTemplate.name;
      sourceCountdownKey = eventTemplate.id;
      sourceTargetDate = eventTemplate.targetDate;
      sourceCountdownEnabled = eventTemplate.countdownEnabled;
    }

    if (sections.length >= 10) {
      toast({
        title: "Maximum events reached",
        description: "You can only have up to 10 checklist events. Please remove an existing event to add a new one.",
        variant: "destructive"
      });
      return;
    }

    // Show loading toast
    const loadingToast = toast({
      title: "Copying checklist...",
      description: "Please wait while we copy the checklist data.",
    });

    try {
      // Validate that template data exists
      if (!sourcePhases || sourcePhases.length === 0) {
        throw new Error('No checklist data available. Please try refreshing the page.');
      }

      // Generate unique section name
      let baseName = `${sourceName} Copy`;
      let counter = 1;
      let newName = baseName;
      
      while (sections.some(section => section.name.toLowerCase() === newName.toLowerCase())) {
        counter++;
        newName = `${baseName} ${counter}`;
      }
      
      // Create new section in database with proper display_order and countdown data
      const { data: newSection, error: sectionError } = await supabase
        .from('checklist_sections')
        .insert({
          user_id: user.id,
          section_name: newName,
          display_order: await calculateUserSectionDisplayOrder(user.id),
          section_type: 'user',
          source_section: sourceType,
          source_template_id: sourceType !== 'sample-triathlon' ? sourceType : null,
          target_date: sourceTargetDate ? sourceTargetDate.toISOString().split('T')[0] : null,
          countdown_enabled: sourceCountdownEnabled
        })
        .select()
        .single();

      if (sectionError) throw sectionError;

      // Create all phases in parallel
      const phaseInserts = sourcePhases.map((originalPhase, index) => ({
        section_id: newSection.id,
        user_id: user.id,
        phase_title: originalPhase.title,
        display_order: index
      }));

      const { data: newPhases, error: phasesError } = await supabase
        .from('checklist_phases')
        .insert(phaseInserts)
        .select();

      if (phasesError) throw phasesError;

      // Create phase mapping for items
      const phaseMapping = new Map();
      const copiedPhases: Phase[] = newPhases.map((newPhase, index) => {
        const originalPhase = sourcePhases[index];
        phaseMapping.set(originalPhase.id, newPhase.id);
        return {
          id: newPhase.id,
          title: newPhase.phase_title
        };
      });

      // Fetch all template items for all phases in a single query
      const originalPhaseIds = sourcePhases.map(p => p.id);
      if (originalPhaseIds.length > 0) {
        const { data: allTemplateItems, error: itemsError } = await supabase
          .from('template_items')
          .select('*')
          .in('phase_id', originalPhaseIds)
          .order('phase_id, display_order');

        if (itemsError) {
          // Errors are logged in development only via secureLog
        } else if (allTemplateItems && allTemplateItems.length > 0) {
          // Prepare bulk insert for all items
          const itemsToInsert = allTemplateItems.map(item => ({
            phase_id: phaseMapping.get(item.phase_id),
            user_id: user.id,
            section_id: newSection.id,
            item_text: item.item_text,
            notes: item.notes,
            is_ready: item.is_ready,
            is_bundled: item.is_bundled,
            is_secured: item.is_secured,
            display_order: item.display_order
          }));

          // Bulk insert all items
          const { error: insertItemsError } = await supabase
            .from('checklist_items')
            .insert(itemsToInsert);

          if (insertItemsError) {
            throw new Error(`Failed to save checklist items: ${insertItemsError.message}`);
          }
        }
      }

      // Add placeholder phase
      copiedPhases.push({ id: `placeholder-${newSection.id}`, title: 'Add Checklist', isPlaceholder: true });

      // Update local state
      const formattedSection: Section = {
        id: newSection.id,
        name: newSection.section_name,
        phases: copiedPhases
      };

      // Add copied section to the end of sections array
      const updatedSections = [...sections, formattedSection];
      
      setSections(updatedSections);

      // Update countdown state so it displays immediately
      setSectionCountdowns(prev => ({
        ...prev,
        [newSection.id]: {
          targetDate: sourceTargetDate,
          enabled: sourceCountdownEnabled
        }
      }));

      // Load items for the copied phases to update preview cards
      const copiedPhaseIds = copiedPhases.filter(p => !p.isPlaceholder).map(p => p.id);
      if (copiedPhaseIds.length > 0) {
        const { data: copiedItems, error: loadItemsError } = await supabase
          .from('checklist_items')
          .select('*')
          .in('phase_id', copiedPhaseIds)
          .eq('user_id', user.id)
          .order('phase_id, display_order');
        
        if (!loadItemsError && copiedItems) {
          const newItemsByPhase: Record<string, ChecklistItem[]> = {};
          copiedItems.forEach(item => {
            if (!newItemsByPhase[item.phase_id]) {
              newItemsByPhase[item.phase_id] = [];
            }
            newItemsByPhase[item.phase_id].push({
              id: item.id,
              item: item.item_text,
              comment: item.notes || ''
            });
          });
          setPhaseItems(prev => ({
            ...prev,
            ...newItemsByPhase
          }));
        }
      }

      // Dismiss loading toast
      loadingToast.dismiss();

      // Track successful template copy with specific template details
      trackEvent('template_copied', {
        source_type: sourceType,
        template_name: sourceName,
        user_authenticated: !!user,
        is_sample_template: sourceType === 'sample-triathlon',
        has_countdown: sourceCountdownEnabled,
        phase_count: sourcePhases.length
      });

      toast({
        title: "Checklist copied!",
        description: `${sourceName} checklist has been copied to your checklists.`,
      });

      // Refresh preloaded user sections for search functionality
      await refetchUserSections();

      // Scroll to the newly copied section
      setNewlyCreatedSectionId(newSection.id);

    } catch (error) {
      // Errors are logged in development only via secureLog
      
      // Dismiss loading toast
      loadingToast.dismiss();
      
      // Provide specific error messages based on error type
      let errorMessage = "There was an error copying the checklist. Please try again.";
      
      if (error instanceof Error) {
        if (error.message.includes('No sample checklist data available')) {
          errorMessage = "Sample checklist data is not loaded. Please refresh the page and try again.";
        } else if (error.message.includes('Failed to save checklist items')) {
          errorMessage = error.message;
        } else if (error.message.includes('duplicate key')) {
          errorMessage = "An event with this name already exists. Please try again.";
        }
      }
      
      toast({
        title: "Copy failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [user, toast, navigate, sampleTriathlonPhases, sampleSectionName, sectionCountdowns, eventTemplatesData, sections, supabase, setSections, setSectionCountdowns, setPhaseItems, trackEvent, refetchUserSections, setNewlyCreatedSectionId]);

  // Initialize onboarding state - only show when Help is clicked
  // Removed auto-display for first-time users
  

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Sink Bike Run",
    "url": "https://sinkbikerun.com",
    "logo": "https://sinkbikerun.com/favicon.svg",
    "description": "Free triathlon race day checklist builder and organizer for athletes of all levels",
    "sameAs": []
  };

  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Sink Bike Run",
    "url": "https://sinkbikerun.com",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Build custom triathlon race day checklists, organize gear by phase, and never forget essential items. Free cloud storage with real-time sync.",
    "featureList": [
      "Custom checklist builder",
      "Proven triathlon templates",
      "Cloud storage and sync",
      "Mobile optimized",
      "Print-ready checklists",
      "Countdown timers",
      "Share with coaches"
    ],
    "screenshot": "https://sinkbikerun.com/og-image.jpg"
  };

  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Sink Bike Run",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className="min-h-screen bg-background">
        <Helmet>
          <title>Free Triathlon Checklist Builder | Sink Bike Run</title>
          <meta name="description" content="Create customizable triathlon race day checklists. Build from scratch or use proven templates. Free, printable, and mobile-optimized for athletes of all levels." />
          <meta name="keywords" content="triathlon checklist, race day preparation, triathlon packing list, race day checklist, triathlon gear list, ironman checklist, sprint triathlon checklist, free triathlon checklist" />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          
          {/* Open Graph tags */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Free Triathlon Checklist Builder | Sink Bike Run" />
          <meta property="og:description" content="Create customizable triathlon race day checklists. Build from scratch or use proven templates. Free, printable, and mobile-optimized." />
          <meta property="og:url" content="https://sinkbikerun.com" />
          <meta property="og:image" content="https://sinkbikerun.com/og-image.jpg" />
          
          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Free Triathlon Checklist Builder | Sink Bike Run" />
          <meta name="twitter:description" content="Create customizable triathlon race day checklists. Build from scratch or use proven templates. Free, printable, and mobile-optimized." />
          <meta name="twitter:image" content="https://sinkbikerun.com/og-image.jpg" />
        </Helmet>
        <CanonicalTag url={canonicalUrl} />
        <StructuredData data={[organizationSchema, webApplicationSchema, softwareApplicationSchema]} />
        <Header
          onSearchClick={() => setIsSearchOpen(true)} 
          isAdminMode={isAdminMode}
          onAdminModeChange={setIsAdminMode}
          showSecurityDashboard={showSecurityDashboard}
          onSecurityDashboardChange={setShowSecurityDashboard}
          showSearch={true}
          onGetStarted={() => setIsOnboardingOpen(true)}
        />
      
      {/* Hero Section - 70vh */}
      <HeroSection 
        user={user}
        isAdmin={isAdmin}
        isAdminMode={isAdminMode}
        onViewSampleAndTemplates={scrollToSampleAndTemplates}
        onViewChecklists={scrollToFirstUserChecklist}
        hasUserChecklists={hasUserChecklists}
        handleAddSection={scrollToCreateSection}
      />
      
      {isPageFullyLoaded ? (
        <main 
          id="main-content" 
          className="max-w-[1280px] mx-auto px-8 md:px-16 mt-9"
          role="main"
          aria-label="Triathlon checklist management"
        >
          {isAdminMode && (
            <>
              <AdminTriathlonPanel />
            </>
          )}
          
          {/* SEO Content - Hidden from visual users, visible to search engines and screen readers */}
          <SeoChecklistContent templates={preloadedTemplates} />

          {/* Sections */}
          <div className="space-y-8" role="region" aria-label="Checklist sections">
              
              {/* Help Text - At the top */}
              {(sampleSectionName || showEventSection) && (
                <p className="text-base md:text-lg font-body text-foreground mb-6">
                  Need ideas? Start with a{' '}
                  <button
                    onClick={scrollToSampleAndTemplates}
                    className="text-red-600 hover:text-red-700 underline underline-offset-2 transition-colors"
                  >
                    template
                  </button>
                  . Prefer full control?{' '}
                  <button
                    onClick={scrollToCreateSection}
                    className="text-red-600 hover:text-red-700 underline underline-offset-2 transition-colors"
                  >
                    Create
                  </button>
                  {' '}from scratch in My Event Checklists.
                </p>
              )}

              {/* User Editable Sections */}
              <div ref={myEventsHeaderRef} className="mb-6 scroll-mt-24">
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-2">
                  My Event Checklists
                </h2>
              <p className="text-sm md:text-base font-body text-muted-foreground">
                Your personal event checklists. Create, edit, and organize your race day preparation.
              </p>
            </div>
            
            {sections.map((section, index) => (
              <div 
                key={section.id} 
                role="region"
                aria-labelledby={`section-${section.id}-heading`}
                ref={(el) => {
                  sectionRefs.current[section.id] = el;
                }}
              >
                <h2 id={`section-${section.id}-heading`} className="sr-only">{section.name} - User Checklist Section</h2>
                <Section
                  id={section.id}
                  name={section.name}
                  phases={section.phases}
                  phaseTitles={phaseTitles}
                  onPhaseSelect={handlePhaseSelect}
                  onSectionNameUpdate={updateSectionName}
                  onRemoveSection={removeSection}
                  onAddTile={addTile}
                  onRemoveTile={removeTile}
                  onReorderPhases={reorderPhases}
                  canRemoveSection={canRemoveSection(section)}
                  onPrintSection={printSectionChecklists}
                  onCopySection={copySectionChecklists}
                  onShareSection={handleShareSection}
                  onClearSectionCheckboxes={handleClearSectionCheckboxes}
                  isLoggedIn={!!user}
                  targetDate={sectionCountdowns[section.id]?.targetDate}
                  countdownEnabled={sectionCountdowns[section.id]?.enabled || false}
                  onCountdownUpdate={handleCountdownUpdate}
                  externalCollapsed={userSectionCollapsed[section.id] || false}
                  onExternalToggleCollapse={toggleUserSectionCallbacks[section.id]}
                  phaseItems={phaseItems}
                />
              </div>
            ))}
              
                {/* Add Event Button - Reserve space to prevent layout shift */}
                {(!user || sections.length < 10) && (
                  <div className="mb-12" ref={firstUserSectionRef}>
                   <div 
                     ref={createEventBoxRef}
                     className="group border-2 border-dashed border-border hover:border-accent rounded-sm p-10 cursor-pointer flex flex-col items-center justify-center text-center bg-card hover:shadow-sm transition-all"
                     style={{ 
                       minHeight: '200px'
                     }}
                     onClick={addSection}
                   >
                     <Plus className="w-8 h-8 md:w-10 md:h-10 lg:w-16 lg:h-16 text-accent mb-3 md:mb-4 lg:mb-6 transition-transform group-hover:scale-110" />
                     <div className="text-muted-foreground font-normal text-lg md:text-2xl lg:text-3xl transition-colors duration-300 group-hover:text-accent">
                       {!user && sections.length >= 1 
                         ? 'Sign up to add more events' 
                         : user 
                           ? `Add Event (${sections.length}/10)`
                           : 'Create your own checklist'}
                     </div>
                     <div className="text-muted-foreground text-sm md:text-lg lg:text-xl font-light mt-1 md:mt-2 lg:mt-3">
                       {!user && sections.length >= 1 
                         ? 'Free account required for multiple events'
                         : 'Click to create a new event'}
                     </div>
                   </div>
                 </div>
               )}

              {/* Checklist Templates Section */}
              {(sampleSectionName || showEventSection) && (
                <div 
                  id="checklist-templates-section"
                  ref={sampleAndTemplatesRef} 
                  className="scroll-mt-24 -mx-8 md:-mx-16 px-8 md:px-16 bg-muted mt-8 py-6 md:py-8"
                >
                  {/* Accordion Toggle Header - always visible */}
                  <button
                    onClick={toggleSamplesAndTemplates}
                    className="w-full flex items-start justify-between py-2 text-left cursor-pointer group"
                    aria-expanded={showSamplesAndTemplates}
                    aria-controls="checklist-templates-content"
                  >
                    <div>
                      <h2 className="text-xl md:text-2xl lg:text-3xl font-display font-semibold text-foreground group-hover:text-accent transition-colors">
                        Checklist Templates
                      </h2>
                      <p className="text-sm md:text-base font-body text-muted-foreground mt-1">
                        Browse proven checklist formats. Each can be copied and customized to fit your needs.
                      </p>
                    </div>
                    <ChevronDown 
                      className={`h-6 w-6 md:h-7 md:w-7 text-muted-foreground group-hover:text-accent transition-transform duration-300 flex-shrink-0 mt-1 ${
                        showSamplesAndTemplates ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Collapsible Content */}
                  <div 
                    id="checklist-templates-content"
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      showSamplesAndTemplates
                        ? 'mt-6 opacity-100 max-h-[5000px]'
                        : 'mt-0 opacity-0 max-h-0 pointer-events-none'
                    }`}
                  >

                  {/* Section Content */}
                  <div className="space-y-6">
                      {/* Sample checklist (independent collapse) */}
                      {sampleSectionName && preloadedTemplates.length > 0 && (
                        <div id="sample-section" role="region" aria-labelledby="sample-section-heading">
                          <h3 id="sample-section-heading" className="sr-only">{sampleSectionName} - Sample Checklist Section</h3>
                          <SharedSection
                            id="sample-section"
                            name={sampleSectionName}
                            phases={sampleTriathlonPhases}
                            phaseTitles={phaseTitles}
                            onPhaseSelect={handlePhaseSelect}
                            targetDate={sectionCountdowns['sample-triathlon']?.targetDate}
                            countdownEnabled={sectionCountdowns['sample-triathlon']?.enabled || false}
                            externalCollapsed={sampleSectionCollapsed || false}
                            onExternalToggleCollapse={toggleSampleSectionCollapse}
                            onCopySection={() => copyTemplateToUserChecklist('sample-triathlon')}
                            phaseItems={samplePhaseItems}
                          />
                        </div>
                      )}

                      {/* Event templates (each with independent collapse) */}
                      {showEventSection && eventTemplatesData.length > 0 && (
                        <div className="space-y-6">
                          {eventTemplatesData.map((template) => (
                            <div key={template.id} id={`event-template-${template.id}`} role="region" aria-labelledby={`event-template-${template.id}-heading`}>
                              <h3 id={`event-template-${template.id}-heading`} className="sr-only">{template.name} - Event Checklist Template</h3>
                              <SharedSection
                                id={`event-template-${template.id}`}
                                name={template.name}
                                phases={template.phases}
                                phaseTitles={phaseTitles}
                                onPhaseSelect={handlePhaseSelect}
                                targetDate={template.targetDate}
                                countdownEnabled={template.countdownEnabled}
                                externalCollapsed={eventTemplatesCollapsed[template.id] || false}
                                onExternalToggleCollapse={() => toggleEventTemplateCollapse(template.id)}
                                onCopySection={() => copyTemplateToUserChecklist(template.id)}
                                onPrintSection={() => printEventTemplate(template.id)}
                                showWatermark={false}
                                phaseItems={template.items}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>

        </main>
      ) : (
        <div className="flex flex-col items-center justify-center min-h-[400px] py-16">
          <Bike className="h-16 w-16 text-primary animate-pulse mb-4" />
          <p className="text-muted-foreground font-body">Loading your checklists...</p>
        </div>
      )}

      {/* Checklist Modal */}
      {selectedPhase && !isModalDataLoading && (
        <>
          {(sampleTriathlonPhases.some(phase => phase.id === selectedPhase) ||
            eventTemplatesData.some(template => template.phases.some(phase => phase.id === selectedPhase))) ? (
            <SampleChecklistModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              phaseId={selectedPhase}
              phaseTitle={selectedPhaseTitle}
              sectionName={
                sampleTriathlonPhases.some(phase => phase.id === selectedPhase)
                  ? sampleSectionName
                  : eventTemplatesData.find(template => 
                      template.phases.some(phase => phase.id === selectedPhase)
                    )?.name || sampleSectionName
              }
              isSampleChecklist={true}
              isSharedChecklist={eventTemplatesData.some(template => 
                template.phases.some(phase => phase.id === selectedPhase)
              )}
              preloadedItems={preloadedModalData}
            />
          ) : (
            <ChecklistModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              phaseId={selectedPhase}
              phaseTitle={selectedPhaseTitle || sections.flatMap(s => s.phases).find(p => p.id === selectedPhase)?.title || ''}
              sectionName={sections.find(s => s.phases.some(p => p.id === selectedPhase))?.name || 'My Event'}
              onPhaseTitleUpdate={updatePhaseTitle}
              preloadedItems={preloadedModalData}
              isSampleChecklist={false}
            />
          )}
        </>
      )}


      {/* Share Modal */}
      {selectedSectionForShare && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedSectionForShare(null);
          }}
          section={selectedSectionForShare.section}
          phases={selectedSectionForShare.phases}
          items={selectedSectionForShare.items}
        />
      )}
      
      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        sections={sections}
        userSections={preloadedUserSections}
        templates={preloadedTemplates}
        phaseTitles={phaseTitles}
        onResultSelect={(sectionId, phaseId, phaseTitle, isTemplate) => {
          // Use handlePhaseSelect to properly load data before opening modal
          handlePhaseSelect(phaseId, phaseTitle, isTemplate);
          setIsSearchOpen(false);
        }}
      />
      
      {/* Onboarding/Help Modal */}
      <OnboardingModal 
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        onComplete={() => {
          localStorage.setItem('onboarding-seen', 'true');
          setIsOnboardingOpen(false);
        }}
      />
      
      <Footer />
      
      {/* Admin Panel with Security Dashboard */}
      <AdminPanel 
        onAdminModeChange={setIsAdminMode} 
        isAdminMode={isAdminMode}
        showSecurityDashboard={showSecurityDashboard}
        onSecurityDashboardChange={setShowSecurityDashboard}
      />
    </div>
  );
};

export default Checklists;